/**
 * The learner's own graph, as a constellation.
 *
 * The plan calls this "the motivational payoff of the whole knowledge-graph design
 * and worth real design attention (kids should want to look at it)".
 *
 * Two rules it is built around:
 *
 *  - **No percentages, anywhere.** Mastery is carried as a number for ordering, but
 *    what is shown is the band word the graph's own author wrote. 0.6 rendered as
 *    "60%" invites two kids to compare scores; the author's word for it is "can do
 *    it with hints".
 *  - **The same graph lays out identically every time.** See skillmap.ts.
 */

import { useMemo, useState } from "react";
import type { CSSProperties } from "react";

import type { SkillMap as SkillMapData } from "../api";
import { describe, hueForStage, layoutMap, radiusFor } from "../skillmap";
import type { PlacedConcept } from "../skillmap";

interface Props {
  map: SkillMapData;
  /** Concept ids that changed in the session just ended, to single out. */
  litUp?: string[];
  onClose?: () => void;
}

export function SkillMap({ map, litUp = [], onClose }: Props) {
  const layout = useMemo(() => layoutMap(map), [map]);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const changed = useMemo(() => new Set(litUp), [litUp]);
  const selected = layout.concepts.find((c) => c.id === selectedId) ?? null;
  const nextUp = layout.concepts.filter((c) => c.readyToStart);

  return (
    <section className="panel skillmap" aria-label="Skill map">
      <header className="panel-head">
        <div className="panel-title">
          {map.display_name} — {map.domain_title}
        </div>
        <div className="actions">
          {onClose && (
            <button type="button" onClick={onClose} className="ghost">
              Close
            </button>
          )}
        </div>
      </header>

      <p className="map-summary">
        <strong>{map.started}</strong> of {map.total} concepts started
        {map.review_due_now > 0 && <> · {map.review_due_now} ready for a review</>}
        {changed.size > 0 && (
          <> · <span className="lit">{changed.size} moved today</span></>
        )}
      </p>

      <ul className="stage-legend">
        {layout.stages.map((name, index) => (
          <li key={name}>
            <span
              className="swatch"
              style={{ "--hue": `${hueForStage(index, layout.stages.length)}` } as CSSProperties}
            />
            {name}
          </li>
        ))}
      </ul>

      <div className="map-canvas">
        <svg
          viewBox={`0 0 ${layout.width} ${layout.height}`}
          width={layout.width}
          height={layout.height}
          role="img"
          aria-label={`${map.total} concepts, ${map.started} started`}
        >
          <g className="edges">
            {layout.edges.map((edge) => (
              <line
                key={`${edge.from}->${edge.to}`}
                x1={edge.x1}
                y1={edge.y1}
                x2={edge.x2}
                y2={edge.y2}
                className={edge.gap ? "edge gap" : "edge"}
              />
            ))}
          </g>
          <g className="nodes">
            {layout.concepts.map((concept) => (
              <ConceptNode
                key={concept.id}
                concept={concept}
                selected={concept.id === selectedId}
                lit={changed.has(concept.id)}
                onSelect={() => setSelectedId(concept.id === selectedId ? null : concept.id)}
              />
            ))}
          </g>
        </svg>
      </div>

      {selected ? (
        <ConceptDetail concept={selected} />
      ) : (
        <div className="map-detail empty">
          {nextUp.length > 0 ? (
            <>
              <div className="detail-title">Ready when you are</div>
              <ul className="next-up">
                {nextUp.slice(0, 4).map((c) => (
                  <li key={c.id}>
                    <button type="button" className="linkish" onClick={() => setSelectedId(c.id)}>
                      {c.label}
                    </button>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="hint">Pick a star to see what it is.</p>
          )}
        </div>
      )}
    </section>
  );
}

function ConceptNode({
  concept,
  selected,
  lit,
  onSelect,
}: {
  concept: PlacedConcept;
  selected: boolean;
  lit: boolean;
  onSelect: () => void;
}) {
  const classes = [
    "node",
    `band-${concept.bandIndex}`,
    concept.unlocked ? "unlocked" : "locked",
    concept.readyToStart ? "ready" : "",
    concept.required_by_project ? "project" : "",
    selected ? "selected" : "",
    lit ? "lit" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <g
      className={classes}
      // Hue is the stage; the band classes turn it into a lightness. Set as a custom
      // property so the palette stays in the stylesheet rather than in JSX.
      style={{ "--hue": `${concept.hue}` } as CSSProperties}
      transform={`translate(${concept.x} ${concept.y})`}
      onClick={onSelect}
      role="button"
      tabIndex={0}
      aria-label={`${concept.label}: ${describe(concept)}`}
      aria-pressed={selected}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect();
        }
      }}
    >
      {lit && <circle className="halo" r={radiusFor(concept) + 8} />}
      <circle className="star" r={radiusFor(concept)} />
      {concept.required_by_project && <circle className="ring" r={radiusFor(concept) + 4} />}
      <text className="label" y={radiusFor(concept) + 15}>
        {concept.label}
      </text>
      {/* Native tooltip, so the information is reachable without JavaScript state. */}
      <title>
        {concept.label} — {describe(concept)}
      </title>
    </g>
  );
}

function ConceptDetail({ concept }: { concept: PlacedConcept }) {
  return (
    <div className="map-detail">
      <div className="detail-title">
        {concept.label}
        {concept.required_by_project && <span className="tag">your project needs this</span>}
      </div>
      <div className="detail-band">{describe(concept)}</div>
      {concept.description && <p className="detail-desc">{concept.description}</p>}

      {concept.evidence && (
        <p className="detail-evidence">
          <span className="detail-label">What you did</span>
          {concept.evidence}
        </p>
      )}

      {concept.prereq_gaps.length > 0 && (
        <p className="detail-gaps">
          <span className="detail-label">First you need</span>
          {concept.prereq_gaps.join(", ")}
        </p>
      )}

      {concept.misconceptions.length > 0 && (
        <p className="detail-watch">
          <span className="detail-label">Worth watching</span>
          {concept.misconceptions.join("; ")}
        </p>
      )}

      {concept.next_review && (
        <p className="detail-review">
          <span className="detail-label">Review</span>
          {concept.next_review}
        </p>
      )}
    </div>
  );
}
